import { useMemo } from 'react';
import { useGetAvailableSpecialtiesQuery } from '@features/user/store/api';
import { Alert, Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

export interface SpecialtyOption {
  specialty_id: string;
  specialty_name: string;
}

interface SpecialtySelectProps {
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  margin?: 'dense' | 'none' | 'normal';
  onChange: (specialty: SpecialtyOption | null) => void;
  required?: boolean;
  selectedSpecialtyId?: string;
  value: string;
}

const LEGACY_OPTION_PREFIX = 'legacy-specialty:';

export function SpecialtySelect({
  disabled = false,
  error = false,
  helperText,
  label = 'Specialty',
  margin = 'none',
  onChange,
  required = false,
  selectedSpecialtyId,
  value,
}: SpecialtySelectProps) {
  const { data, error: specialtiesError, isLoading } = useGetAvailableSpecialtiesQuery(undefined);
  const canonicalOptions = useMemo<SpecialtyOption[]>(
    () =>
      (data?.specialties ?? [])
        .filter(specialty => !('hidden' in specialty && specialty.hidden === true))
        .map(specialty => ({
          specialty_id: specialty.specialty_id,
          specialty_name: specialty.specialty_name,
        })),
    [data?.specialties],
  );
  const canonicalValue = selectedSpecialtyId
    ? canonicalOptions.find(option => option.specialty_id === selectedSpecialtyId)
    : canonicalOptions.find(option => option.specialty_name === value);
  const legacyValue =
    value && !canonicalValue
      ? { specialty_id: `${LEGACY_OPTION_PREFIX}${value}`, specialty_name: value }
      : null;
  const options = legacyValue ? [legacyValue, ...canonicalOptions] : canonicalOptions;
  const selectedValue = canonicalValue ?? legacyValue;
  const hasNoOptions = !isLoading && !specialtiesError && canonicalOptions.length === 0;

  if (specialtiesError) {
    return <Alert severity="error">Specialties could not be loaded. Try again.</Alert>;
  }

  return (
    <Box>
      <Autocomplete<SpecialtyOption>
        fullWidth
        disabled={disabled || isLoading || hasNoOptions}
        loading={isLoading}
        options={options}
        value={selectedValue}
        getOptionLabel={option => option.specialty_name}
        isOptionEqualToValue={(option, selected) => option.specialty_id === selected.specialty_id}
        onChange={(_event, specialty) => onChange(specialty)}
        renderOption={(props, option) => {
          const { key: _key, ...optionProps } = props;
          return (
            <Box component="li" key={option.specialty_id} {...optionProps}>
              {option.specialty_name}
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            error={error}
            helperText={helperText}
            label={label}
            margin={margin}
            required={required}
            size="small"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoading && (
                      <CircularProgress
                        aria-label="Loading specialties"
                        color="inherit"
                        size={18}
                      />
                    )}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
              htmlInput: {
                ...params.slotProps.htmlInput,
                'aria-label': label,
              },
            }}
          />
        )}
      />
      {hasNoOptions && (
        <Box role="status" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
          No specialties are available. Try again later.
        </Box>
      )}
    </Box>
  );
}
